// Everything in this file runs only in the browser. Photos never leave the device.
import { jsPDF } from "jspdf";

export const MAX_PHOTOS = 3;
const MAX_SIDE = 1600;
const JPEG_QUALITY = 0.82;

export function checkPrivateSupport() {
  try {
    const c = document.createElement("canvas");
    c.width = 4;
    c.height = 4;
    const ctx = c.getContext("2d");
    if (!ctx) return false;
    ctx.fillRect(0, 0, 4, 4);
    if (!c.toDataURL("image/jpeg", 0.8).startsWith("data:image/jpeg")) return false;
    return typeof Blob !== "undefined" && typeof FileReader !== "undefined"
      && !!(window.URL && URL.createObjectURL) && "download" in document.createElement("a");
  } catch {
    return false;
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export function rotatedSize(w, h, rotation) {
  const r = (rotation * Math.PI) / 180;
  return {
    width: Math.round(Math.abs(Math.cos(r) * w) + Math.abs(Math.sin(r) * h)),
    height: Math.round(Math.abs(Math.sin(r) * w) + Math.abs(Math.cos(r) * h)),
  };
}

// Rotate + crop + downscale + re-encode as JPEG (re-encoding drops EXIF/location metadata).
export async function processPhoto(src, rotation = 0, crop = null) {
  const img = await loadImage(src);
  const { width: bw, height: bh } = rotatedSize(img.naturalWidth, img.naturalHeight, rotation);
  const full = document.createElement("canvas");
  full.width = bw;
  full.height = bh;
  const fctx = full.getContext("2d");
  fctx.translate(bw / 2, bh / 2);
  fctx.rotate((rotation * Math.PI) / 180);
  fctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);

  const c = crop || { x: 0, y: 0, width: bw, height: bh };
  const scale = Math.min(1, MAX_SIDE / Math.max(c.width, c.height));
  const out = document.createElement("canvas");
  out.width = Math.max(1, Math.round(c.width * scale));
  out.height = Math.max(1, Math.round(c.height * scale));
  const octx = out.getContext("2d");
  octx.fillStyle = "#ffffff";
  octx.fillRect(0, 0, out.width, out.height);
  octx.drawImage(full, c.x, c.y, c.width, c.height, 0, 0, out.width, out.height);
  full.width = 0;
  full.height = 0;
  return { dataUrl: out.toDataURL("image/jpeg", JPEG_QUALITY), width: out.width, height: out.height };
}

const SAFE = /[^\u0020-\u00FF\u2018\u2019\u201C\u201D\u2013\u2014\u2026\u2022\u2122\u20AC\n]/g;
const clean = (t) => String(t || "").replace(SAFE, "");

export function docRef(k) {
  const d = k.created_at ? new Date(k.created_at) : new Date();
  const ymd = `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, "0")}${String(d.getUTCDate()).padStart(2, "0")}`;
  return `YLJM-${(k.id || "").slice(-6).toUpperCase() || "000000"}-${ymd}`;
}

// photos: [{dataUrl,width,height,target}] where target = "cover" | "season:<name>" | "memory:<id>"
// memoryInfo: { [id]: { title, season } }
export function buildEbookPdf({ keepsake, author, photos, memoryInfo }) {
  const pdf = new jsPDF({ unit: "mm", format: "a5", compress: true });
  const W = 148, H = 210, M = 18, CW = W - 2 * M, BOTTOM = H - 22;
  const ref = docRef(keepsake);
  const created = new Date(keepsake.created_at || Date.now())
    .toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
  const photoFor = (t) => photos.filter((p) => p.target === t);
  let y = M;

  const newPage = () => { pdf.addPage(); y = M + 4; };
  const ensure = (h) => { if (y + h > BOTTOM) newPage(); };
  const para = (text, { size = 10.5, style = "normal", lead = 5.4, align = "left", color = [44, 42, 40] } = {}) => {
    pdf.setFont("times", style);
    pdf.setFontSize(size);
    pdf.setTextColor(...color);
    clean(text).split("\n").filter((p) => p.trim()).forEach((p) => {
      pdf.splitTextToSize(p.trim(), CW).forEach((line) => {
        ensure(lead);
        pdf.text(line, align === "center" ? W / 2 : M, y, { align });
        y += lead;
      });
      y += lead * 0.5;
    });
  };
  const image = (p, caption) => {
    const ratio = Math.min(CW / p.width, 85 / p.height);
    const w = p.width * ratio, h = p.height * ratio;
    ensure(h + (caption ? 9 : 4));
    pdf.addImage(p.dataUrl, "JPEG", (W - w) / 2, y, w, h, undefined, "FAST");
    y += h + 3;
    if (caption) {
      pdf.setFont("times", "italic");
      pdf.setFontSize(9);
      pdf.setTextColor(138, 127, 118);
      pdf.text(clean(caption), W / 2, y + 1.5, { align: "center", maxWidth: CW });
      y += 7;
    }
    y += 2;
  };

  // Cover
  pdf.setFillColor(46, 59, 49);
  pdf.rect(0, 0, W, H, "F");
  const cover = photoFor("cover")[0];
  let cy = 70;
  if (cover) {
    const ratio = Math.min(100 / cover.width, 95 / cover.height);
    const w = cover.width * ratio, h = cover.height * ratio;
    pdf.addImage(cover.dataUrl, "JPEG", (W - w) / 2, 28, w, h, undefined, "FAST");
    cy = 28 + h + 16;
  }
  pdf.setTextColor(251, 244, 232);
  pdf.setFont("times", "bold");
  pdf.setFontSize(24);
  const tl = pdf.splitTextToSize(clean(keepsake.title), CW);
  pdf.text(tl, W / 2, cy, { align: "center" });
  cy += tl.length * 10 + 2;
  if (author) {
    pdf.setFont("times", "italic");
    pdf.setFontSize(13);
    pdf.text(clean(author), W / 2, cy, { align: "center" });
  }
  pdf.setFont("times", "normal");
  pdf.setFontSize(9);
  pdf.text("Your Life Journeys Matter\u2122 \u00B7 A BrightHarbor creation", W / 2, H - 16, { align: "center" });

  // Title page
  newPage();
  y = 75;
  para(keepsake.title, { size: 20, style: "bold", lead: 9, align: "center" });
  y += 4;
  para("Your Life Journeys Matter\u2122", { size: 12, style: "italic", align: "center", color: [176, 141, 87] });
  para("A BrightHarbor creation", { size: 10, align: "center", color: [138, 127, 118] });
  y += 8;
  para(`Document reference ${ref}\nCreated ${created}`, { size: 9, align: "center", color: [138, 127, 118] });

  if (keepsake.dedication) {
    newPage();
    y = 80;
    para(keepsake.dedication, { size: 13, style: "italic", lead: 7, align: "center" });
  }

  // Contents placeholder — filled in after pages are known
  newPage();
  const contentsPage = pdf.getNumberOfPages();
  const toc = [];

  if (keepsake.intro) {
    newPage();
    toc.push({ label: "Prologue", page: pdf.getNumberOfPages() });
    para("Prologue", { size: 16, style: "bold", lead: 8 });
    y += 2;
    para(keepsake.intro, { style: "italic" });
  }

  (keepsake.sections || []).forEach((sec) => {
    newPage();
    toc.push({ label: sec.heading, page: pdf.getNumberOfPages() });
    para(sec.heading, { size: 16, style: "bold", lead: 8, color: [168, 74, 50] });
    y += 2;
    if (sec.season) photoFor(`season:${sec.season}`).forEach((p) => image(p));
    para(sec.body);
    if (sec.season) {
      photos.filter((p) => p.target?.startsWith("memory:")
        && memoryInfo[p.target.slice(7)]?.season === sec.season)
        .forEach((p) => image(p, memoryInfo[p.target.slice(7)]?.title));
    }
  });

  // Contents
  pdf.setPage(contentsPage);
  y = M + 10;
  para("Contents", { size: 18, style: "bold", lead: 9 });
  y += 4;
  pdf.setFont("times", "normal");
  pdf.setFontSize(11);
  pdf.setTextColor(44, 42, 40);
  toc.forEach((t) => {
    const label = pdf.splitTextToSize(clean(t.label), CW - 14)[0];
    pdf.text(label, M, y);
    pdf.text(String(t.page), W - M, y, { align: "right" });
    y += 8;
  });

  // Footer on every page except the cover
  const total = pdf.getNumberOfPages();
  for (let i = 2; i <= total; i += 1) {
    pdf.setPage(i);
    pdf.setFont("times", "normal");
    pdf.setFontSize(7);
    pdf.setTextColor(138, 127, 118);
    pdf.text("\u00A9 2026 BrightHarbor \u00B7 Your Life Journeys Matter\u2122 \u00B7 All rights reserved", W / 2, H - 11, { align: "center" });
    pdf.text(`Ref ${ref} \u00B7 Created ${created} \u00B7 Page ${i}`, W / 2, H - 7.5, { align: "center" });
  }

  pdf.save(`${clean(keepsake.title).replace(/[^a-z0-9]+/gi, "-") || "my-life-story"}.pdf`);
}
